import { useMemo } from 'react';
import type { CrawlSchedule, WithResourceId } from '../../../types/knowledge';
import type { DirectoryUser } from '../../../types/user';
import { isTerminalCrawlStatus } from '../knowledgePolling';
import { useKnowledgeCrawlSchedulesQuery } from '../queries/useKnowledgeCrawlSchedulesQuery';
import { useKnowledgeCrawlsQuery } from '../queries/useKnowledgeCrawlsQuery';
import { useTenantUsersQuery } from '../../tenant/queries/useTenantUsersQuery';
import { useCrawlStatusStreams } from './useCrawlStatusStreams';
import { useKnowledgeCrawlsTabStore } from '../stores/useKnowledgeCrawlsTabStore';
import { useGroupedCrawlsByStatus } from './groupCrawlsByStatus';
import { useCrawlListActions } from './useCrawlListActions';

type UseCrawlListsOptions = {
  databaseId: string;
};

export function useCrawlLists({ databaseId }: UseCrawlListsOptions) {
  const crawlsQuery = useKnowledgeCrawlsQuery({
    databaseId,
    enabled: true,
  });
  const schedulesQuery = useKnowledgeCrawlSchedulesQuery({
    databaseId,
    enabled: true,
  });
  const directoryUsersQuery = useTenantUsersQuery({
    enabled: true,
  });

  const isMappingExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.isMappingExpanded,
  );
  const isNeedsAttentionExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.isNeedsAttentionExpanded,
  );
  const isInProgressExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.isInProgressExpanded,
  );
  const isCompletedExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.isCompletedExpanded,
  );
  const isFailedExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.isFailedExpanded,
  );
  const toggleMappingExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.toggleMappingExpanded,
  );
  const toggleNeedsAttentionExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.toggleNeedsAttentionExpanded,
  );
  const toggleInProgressExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.toggleInProgressExpanded,
  );
  const toggleCompletedExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.toggleCompletedExpanded,
  );
  const toggleFailedExpanded = useKnowledgeCrawlsTabStore(
    (state) => state.toggleFailedExpanded,
  );
  const expandAllCrawlGroups = useKnowledgeCrawlsTabStore(
    (state) => state.expandAllCrawlGroups,
  );
  const collapseAllCrawlGroups = useKnowledgeCrawlsTabStore(
    (state) => state.collapseAllCrawlGroups,
  );

  const crawls = useMemo(() => crawlsQuery.data ?? [], [crawlsQuery.data]);

  const nonTerminalCrawlIds = useMemo(
    () =>
      crawls
        .filter((crawl) => !isTerminalCrawlStatus(crawl.status ?? undefined))
        .map((crawl) => crawl.id),
    [crawls],
  );

  const {
    liveStatusByCrawlId,
    clearLiveStatusForCrawl,
    isCrawlStatusStreamActive,
    restartCrawlStatusStreamIfStopped,
    restartActiveCrawlStatusStreams,
  } = useCrawlStatusStreams({
    databaseId,
    crawlIds: nonTerminalCrawlIds,
  });

  const crawlSchedules = useMemo(
    () => schedulesQuery.data ?? [],
    [schedulesQuery.data],
  );

  const {
    crawlsMapping,
    crawlsNeedingAttention,
    crawlsInProgress,
    crawlsCompleted,
    crawlsFailed,
  } = useGroupedCrawlsByStatus(crawls, liveStatusByCrawlId);

  const crawlScheduleByJobId = useMemo(() => {
    const map = new Map<string, WithResourceId<CrawlSchedule>>();
    for (const schedule of crawlSchedules) {
      if (schedule.crawlJobId) {
        map.set(schedule.crawlJobId, schedule);
      }
    }
    return map;
  }, [crawlSchedules]);

  const directoryUsersById = useMemo(() => {
    const map = new Map<string, DirectoryUser>();
    for (const directoryUser of directoryUsersQuery.data ?? []) {
      if (directoryUser.id) {
        map.set(directoryUser.id, directoryUser);
      }
    }
    return map;
  }, [directoryUsersQuery.data]);

  const actions = useCrawlListActions({
    databaseId,
    crawlsQuery,
    schedulesQuery,
    crawlScheduleByJobId,
    clearLiveStatusForCrawl,
    restartCrawlStatusStreamIfStopped,
    restartActiveCrawlStatusStreams,
  });

  return {
    crawls,
    liveStatusByCrawlId,
    isCrawlStatusStreamActive,
    restartCrawlStatusStreamIfStopped,
    handleCrawlContinuedAfterSelection:
      actions.handleCrawlContinuedAfterSelection,
    crawlsMapping,
    crawlsNeedingAttention,
    crawlsInProgress,
    crawlsCompleted,
    crawlsFailed,
    crawlScheduleByJobId,
    directoryUsersById,
    isMappingExpanded,
    isNeedsAttentionExpanded,
    isInProgressExpanded,
    isCompletedExpanded,
    isFailedExpanded,
    toggleMappingExpanded,
    toggleNeedsAttentionExpanded,
    toggleInProgressExpanded,
    toggleCompletedExpanded,
    toggleFailedExpanded,
    expandAllCrawlGroups,
    collapseAllCrawlGroups,
    handleManualRefresh: actions.handleManualRefresh,
    handleRestartCrawl: actions.handleRestartCrawl,
    handleRetryFailedCrawl: actions.handleRetryFailedCrawl,
    handleResumeAbandonedCrawl: actions.handleResumeAbandonedCrawl,
    handleRequestDeleteCrawl: actions.handleRequestDeleteCrawl,
    pendingDelete: actions.pendingDelete,
    setPendingDelete: actions.setPendingDelete,
    confirmDeleteCrawl: actions.confirmDeleteCrawl,
    isDeletingCrawl: actions.isDeletingCrawl,
  };
}
